import React, {useState, useMemo, useRef} from 'react';
import {Box, Typography, Grid} from '@mui/material';
import Button from 'components/button/Button';
import LoaderWithBackdrop from 'components/LoaderWithBackdrop';
import {useTranslation} from 'react-i18next';
import SearchBarController from 'controllers/SearchBarController/SearchBarController';
import GqlTableController from 'controllers/GqlTableController/GqlTableController';
import DateRangePicker from 'components/DateRangePicker';
import DropDownController from 'controllers/DropDownController/DropDownController';
import {
  GET_ABSENCE_SUMMARY,
  GET_ABSENCE_SUMMARY_REPORT,
  FILTER_USERS,
  GET_CATALOGS_BY_TYPE,
} from 'graphql/absences/Querys';
import Chip from 'components/Chip/Chip';
import {DropDownItem} from 'components/DropDownInput/DropDownInput';
import {useGraphqlQuery} from 'hooks/useGraphqlQuery';
import {formatDate} from 'commons/dates';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  country: string;
}

interface Response {
  balance?: {
    vacationDays?: number;
    additionalLeaveDays?: number;
    nextVacationsToExpire?: number;
    nextAdditionalLeaveToExpire?: number;
    vacationDaysExpired?: number;
    vacationDaysTaken?: number;
  };
  bussinessUnit: string;
  client?: string;
  country?: string;
  lastname?: string;
  name?: string;
  rol?: string;
  supervisor?: string;
}

const getDayByKey = (key: string, row: Response): number => {
  switch (key) {
    case 'usedVacations':
      return row.balance?.vacationDaysTaken || 0;
    case 'availableVacations':
      return row.balance?.vacationDays || 0;
    case 'additionalLeave':
      return row.balance?.additionalLeaveDays || 0;
    case 'daysToExpire':
      return row.balance?.nextVacationsToExpire || 0;
    case 'nextAdditionalLeaveToExpire':
      return row.balance?.nextAdditionalLeaveToExpire || 0;
    default:
      return 0;
  }
};

type SummaryFilter = {
  toDate?: string | null;
  ids?: string[] | null;
  country?: string | null;
};

const isSameCalendarDay = (left?: Date | null, right?: Date | null) => {
  if (!left || !right) {
    return left === right;
  }

  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
};

const normalizeFilter = (filter: SummaryFilter) => {
  return JSON.stringify({
    toDate: filter.toDate || null,
    ids: filter.ids || null,
    country: filter.country || null,
  });
};

/**
 * Renders the absence summary screen and exports the selected reference date as a CSV file.
 *
 * @returns The absence summary management view.
 */
const AbsenceSummary: React.FC = () => {
  const {t} = useTranslation();
  const initialDateRef = useRef(new Date());
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<DropDownItem | null>(
    null
  );
  const [tableFilters, setTableFilters] = useState<SummaryFilter>({});
  const [dateRange, setDateRange] = useState<{
    from: Date | null;
    to: Date | null;
  }>({
    from: new Date(),
    to: null,
  });
  const {fetchData: fetchAbsenceSummaryReport, isLoading: isReportLoading} =
    useGraphqlQuery('', GET_ABSENCE_SUMMARY_REPORT, 'absenceSummaryReport');

  const draftFilters = useMemo((): SummaryFilter => {
    const data: SummaryFilter = {};

    if (
      dateRange.from &&
      !isSameCalendarDay(dateRange.from, initialDateRef.current)
    ) {
      data.toDate = formatDate(dateRange.from);
    }

    if (selectedUsers.length > 0) {
      data.ids = selectedUsers.map(user => user.id);
    }

    if (
      selectedCountry &&
      selectedCountry.description.trim() !== '' &&
      selectedCountry.key !== ''
    ) {
      data.country = selectedCountry.description || '';
    }

    return data;
  }, [dateRange.from, selectedCountry, selectedUsers]);

  const isFilterButtonEnabled = useMemo(() => {
    return normalizeFilter(draftFilters) !== normalizeFilter(tableFilters);
  }, [draftFilters, tableFilters]);

  // Columns config using i18n
  const columns = [
    {
      id: 'name',
      label: t('AbsenceSummary.columns.name'),
      render: (value: unknown, row: Response) => {
        return `${row.name} ${row.lastname}`;
      },
    },
    {id: 'country', label: t('AbsenceSummary.columns.country')},
    {
      id: 'usedVacations',
      label: t('AbsenceSummary.columns.usedVacations'),
      render: (value: unknown, row: Response) =>
        getDayByKey('usedVacations', row),
    },
    {
      id: 'availableVacations',
      label: t('AbsenceSummary.columns.availableVacations'),
      render: (value: unknown, row: Response) =>
        getDayByKey('availableVacations', row).toFixed(2),
    },
    {
      id: 'daysToExpire',
      label: t('AbsenceSummary.columns.daysToExpire'),
      render: (value: unknown, row: Response) =>
        getDayByKey('daysToExpire', row).toFixed(2),
    },
    {
      id: 'additionalLeave',
      label: t('AbsenceSummary.columns.additionalLeave'),
      render: (value: unknown, row: Response) =>
        getDayByKey('additionalLeave', row),
    },
  ];

  const downloadCsvReport = (reportContent: string) => {
    if (!reportContent) {
      return;
    }

    const date = new Date().toISOString().split('T')[0];
    const fileName = `absence-summary-report-${date}.csv`;

    // Build a local file so the browser can download the inline GraphQL payload
    // with a stable filename and without depending on cloud storage URLs.
    const blob = new Blob([reportContent], {type: 'text/csv;charset=utf-8'});
    // eslint-disable-next-line node/no-unsupported-features/node-builtins
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // eslint-disable-next-line node/no-unsupported-features/node-builtins
    URL.revokeObjectURL(blobUrl);
  };

  // Intentionally uses the picker date (dateRange.from) and not tableFilters,
  // so the report always reflects the currently selected reference date
  // regardless of whether the user has pressed "Apply".
  const getReportFilter = (): SummaryFilter => {
    if (!dateRange.from) {
      return {};
    }

    return {
      toDate: formatDate(dateRange.from),
    };
  };

  return (
    <Box sx={{p: 3, maxWidth: 1200, margin: '0 auto'}}>
      <LoaderWithBackdrop isLoading={!!isReportLoading} />
      <Typography variant="h5">{t('AbsenceSummary.title')}</Typography>
      <Box sx={{mb: 4, mt: 4}}>
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-around"
        >
          <Grid item xs={12} md={3}>
            <br />
            <SearchBarController<User>
              query={FILTER_USERS}
              queryVariableName="keyword"
              queryDataPath={['filterUsers']}
              placeholder={t('AbsenceSummary.search')}
              transformResult={user => ({
                id: user.id,
                text: `${user.firstName} ${user.lastName}`,
                selected: selectedUsers.some(
                  selected => selected.id === user.id
                ),
              })}
              onResultSelect={user => {
                setSelectedUsers(prev => {
                  const exists = prev.some(u => u.id === user.id);
                  if (exists) {
                    return prev.filter(u => u.id !== user.id);
                  }
                  return [...prev, user];
                });
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" sx={{mb: 1, color: 'text.secondary'}}>
              {t('AbsenceSummary.filterCountry')}
            </Typography>
            <DropDownController
              query={GET_CATALOGS_BY_TYPE}
              queryVariables={{
                type: 'COUNTRY_CODE',
              }}
              rootField="catalogsByType"
              transformResult={item => ({
                key: item.id,
                description: item.name,
              })}
              defaultOption={{
                key: '',
                description: t('AbsenceSummary.allCountries'),
              }}
              value={selectedCountry ? String(selectedCountry.key) || '' : ''}
              onChange={value => {
                setSelectedCountry(value);
              }}
              placeholder={t('leaves.Reports.country')}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <DateRangePicker
              selectionType="single"
              label={t('AbsenceSummary.filterDate')}
              defaultValue={dateRange.from as Date}
              onChangeDates={date =>
                setDateRange({
                  from: date as Date,
                  to: null,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <br />
            <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
              <Button
                buttonStyle="primarySolidShawdow"
                style={{height: 40}}
                disabled={!isFilterButtonEnabled}
                onClickButton={() => {
                  setTableFilters(draftFilters);
                }}
                text={t('AbsenceSummary.applyFilter')}
              />
              <Button
                buttonStyle="primaryOutline"
                style={{height: 40}}
                onClickButton={async () => {
                  try {
                    const reportContent = (await fetchAbsenceSummaryReport({
                      filter: getReportFilter(),
                    })) as string;
                    downloadCsvReport(reportContent);
                  } catch (error) {
                    console.error(
                      'Failed to generate absence summary report',
                      error
                    );
                  }
                }}
                text={t('AbsenceSummary.generateReport')}
              />
            </Box>
          </Grid>
        </Grid>
        {selectedUsers.length > 0 && (
          <Box sx={{mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1}}>
            {selectedUsers.map(user => (
              <Chip
                key={user.id}
                id={user.id}
                text={`${user.firstName} ${user.lastName}`}
                onRemove={id => {
                  setSelectedUsers(prev => prev.filter(u => u.id !== id));
                }}
                color="default"
                size="medium"
              />
            ))}
          </Box>
        )}
      </Box>
      <GqlTableController
        updateTrigger={tableFilters}
        queryConfiguration={{
          query: GET_ABSENCE_SUMMARY,
          rootField: 'getAbsenceSummary',
          queryVariables: (page: number, size: number) => ({
            page,
            size,
            filter: {
              toDate: tableFilters.toDate,
              ids: tableFilters.ids,
              country: tableFilters.country,
            },
          }),
        }}
        columns={columns}
      />
    </Box>
  );
};

export default AbsenceSummary;
